/* tslint:disable */
/* eslint-disable */
import * as runtime from '../runtime';
import type { TariffCode, CustomsEstimate, TariffImportResult } from '../models/index';
import {
    TariffCodeFromJSON,
    TariffCodeToJSON,
    CustomsEstimateFromJSON,
} from '../models/index';

export interface TariffCodeSearchRequest {
    /** Text ("laptop", "running shoes") or a code prefix ("8517.13"). Optional when categoryName is set. */
    q?: string;
    limit?: number;
    categoryName?: string;
}

export interface TariffCodeCalculateCustomsRequest {
    courierId: number;
    tariffCode: string;
    itemValueUsd: number;
}

export interface TariffCodeImportBundledRequest {
    dryRun?: boolean;
    overwriteEdited?: boolean;
}

export interface TariffCodeFindRequest {
    filter?: string;
}

export interface TariffCodeCountRequest {
    where?: string;
}

export interface TariffCodePatchAttributesRequest {
    id: number;
    data: Partial<TariffCode>;
}

/**
 * TariffCode API (/TariffCodes). search + calculateCustoms are public (x-api-key);
 * find/count/patch/import are Overlord-only.
 */
export class TariffCodeApi extends runtime.BaseAPI {

    async tariffCodeSearchRaw(requestParameters: TariffCodeSearchRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<TariffCode>>> {
        const queryParameters: any = {};
        if (requestParameters['q'] != null) {
            queryParameters['q'] = requestParameters['q'];
        }
        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }
        if (requestParameters['categoryName'] != null) {
            queryParameters['categoryName'] = requestParameters['categoryName'];
        }
        const headerParameters: runtime.HTTPHeaders = {};
        const response = await this.request({
            path: `/TariffCodes/search`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(TariffCodeFromJSON));
    }

    async tariffCodeSearch(requestParameters: TariffCodeSearchRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<TariffCode>> {
        const response = await this.tariffCodeSearchRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async tariffCodeCalculateCustomsRaw(requestParameters: TariffCodeCalculateCustomsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<CustomsEstimate>> {
        for (const key of ['courierId', 'tariffCode', 'itemValueUsd'] as const) {
            if (requestParameters[key] == null) {
                throw new runtime.RequiredError(key, `Required parameter "${key}" was null or undefined when calling tariffCodeCalculateCustoms().`);
            }
        }
        const queryParameters: any = {
            courierId: requestParameters['courierId'],
            tariffCode: requestParameters['tariffCode'],
            itemValueUsd: requestParameters['itemValueUsd'],
        };
        const headerParameters: runtime.HTTPHeaders = {};
        const response = await this.request({
            path: `/TariffCodes/calculate-customs`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => CustomsEstimateFromJSON(jsonValue));
    }

    async tariffCodeCalculateCustoms(requestParameters: TariffCodeCalculateCustomsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<CustomsEstimate> {
        const response = await this.tariffCodeCalculateCustomsRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async tariffCodeImportBundledRaw(requestParameters: TariffCodeImportBundledRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<TariffImportResult>> {
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';
        const response = await this.request({
            path: `/TariffCodes/import-bundled`,
            method: 'POST',
            headers: headerParameters,
            query: {},
            body: {
                dryRun: requestParameters['dryRun'] !== false,
                overwriteEdited: requestParameters['overwriteEdited'] === true,
            },
        }, initOverrides);
        return new runtime.JSONApiResponse(response);
    }

    async tariffCodeImportBundled(requestParameters: TariffCodeImportBundledRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<TariffImportResult> {
        const response = await this.tariffCodeImportBundledRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async tariffCodeFindRaw(requestParameters: TariffCodeFindRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<TariffCode>>> {
        const queryParameters: any = {};
        if (requestParameters['filter'] != null) {
            queryParameters['filter'] = requestParameters['filter'];
        }
        const headerParameters: runtime.HTTPHeaders = {};
        const response = await this.request({
            path: `/TariffCodes`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(TariffCodeFromJSON));
    }

    async tariffCodeFind(requestParameters: TariffCodeFindRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<TariffCode>> {
        const response = await this.tariffCodeFindRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async tariffCodeCountRaw(requestParameters: TariffCodeCountRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<{ count: number }>> {
        const queryParameters: any = {};
        if (requestParameters['where'] != null) {
            queryParameters['where'] = requestParameters['where'];
        }
        const headerParameters: runtime.HTTPHeaders = {};
        const response = await this.request({
            path: `/TariffCodes/count`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);
        return new runtime.JSONApiResponse(response);
    }

    async tariffCodeCount(requestParameters: TariffCodeCountRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<{ count: number }> {
        const response = await this.tariffCodeCountRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async tariffCodePatchAttributesRaw(requestParameters: TariffCodePatchAttributesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<TariffCode>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError('id', 'Required parameter "id" was null or undefined when calling tariffCodePatchAttributes().');
        }
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';
        const body = TariffCodeToJSON(requestParameters['data']);
        Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);
        const response = await this.request({
            path: `/TariffCodes/${encodeURIComponent(String(requestParameters['id']))}`,
            method: 'PATCH',
            headers: headerParameters,
            query: {},
            body,
        }, initOverrides);
        return new runtime.JSONApiResponse(response, (jsonValue) => TariffCodeFromJSON(jsonValue));
    }

    async tariffCodePatchAttributes(requestParameters: TariffCodePatchAttributesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<TariffCode> {
        const response = await this.tariffCodePatchAttributesRaw(requestParameters, initOverrides);
        return await response.value();
    }
}
